import Modal from "@/components/controls/Modal";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { Add, PeopleBadge } from "@dreamhub/designsystem/icons";
import CreatePersonModal from "@/components/layouts/CreatePersonModal";
import {
  usePeopleInfinite,
  useResetPeopleInfinite
} from "@/entities/people/hooks";
import { CheckboxGroup } from "@/components/ui/checkbox-group";
import SearchInput from "@/components/ui/search-input";
import { useCompanies } from "@/pages/companies/hooks";
import InlineLoader from "@/components/ui/inline-loader";
import { useInfiniteScrollTrigger } from "@/hooks/useInfiniteScrollTrigger";
import { useDeals, useUpdateDealMutation } from "@/pages/deals/hooks";
import { isReservedPersonId } from "@/entities/people/types";
import { useQueryClient } from "@tanstack/react-query";
import PersonCheckbox from "@/components/layouts/AddPeopleModal/PersonCheckbox";
import { filter, includes, isEmpty, map, union, without } from "lodash";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { getEnumTKey } from "@/utils/strings";
import AddPartnerContactsModal from "@/components/layouts/AddPeopleModal/AddPartnerContactsModal";
import PartnerContactCheckbox from "@/components/layouts/AddPeopleModal/PartnerContactCheckbox";
import { usePartnerContactsInfinite } from "@/pages/partners/contacts/hooks";
import { PartnerContactColumn } from "@/pages/partners/contacts/types";
import { ComputedFields } from "@/services/api/types";

export type AddPeopleMode = "people" | "partnerContacts";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  entityId: string;
  companyId: string;
  allDealPeople?: string[] | null | undefined;
  resetPage?: () => void;
  onAddPeople?: (newPeopleIds: string[]) => Promise<void>;
  entityType?: EntityType;
  mode?: AddPeopleMode;
  partnerId?: string | null;
  onSelectPartner?: (partnerId: string) => Promise<void>;
  addedPartnerContacts?: string[] | null | undefined;
  onAddPartnerContacts?: (newContactIds: string[]) => Promise<void>;
}

const AddPeopleModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  entityId,
  companyId,
  allDealPeople,
  resetPage,
  onAddPeople,
  entityType = EntityType.DEAL,
  mode = "people",
  partnerId,
  onSelectPartner,
  addedPartnerContacts,
  onAddPartnerContacts
}) => {
  if (mode === "partnerContacts") {
    return (
      <AddPartnerContactsModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        entityId={entityId}
        partnerId={partnerId}
        onSelectPartner={onSelectPartner}
        addedPartnerContacts={addedPartnerContacts}
        resetPage={resetPage}
        onAddPartnerContacts={onAddPartnerContacts}
        entityType={entityType}
      />
    );
  }

  return (
    <AddPeopleModalContent
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      entityId={entityId}
      companyId={companyId}
      allDealPeople={allDealPeople}
      resetPage={resetPage}
      onAddPeople={onAddPeople}
      entityType={entityType}
      partnerId={partnerId}
      addedPartnerContacts={addedPartnerContacts}
      onAddPartnerContacts={onAddPartnerContacts}
    />
  );
};

interface ContentProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  entityId: string;
  companyId: string;
  allDealPeople?: string[] | null | undefined;
  resetPage?: () => void;
  onAddPeople?: (newPeopleIds: string[]) => Promise<void>;
  entityType?: EntityType;
  partnerId?: string | null;
  addedPartnerContacts?: string[] | null | undefined;
  onAddPartnerContacts?: (newContactIds: string[]) => Promise<void>;
}

const AddPeopleModalContent: React.FC<ContentProps> = ({
  isOpen,
  setIsOpen,
  entityId,
  companyId,
  allDealPeople,
  resetPage,
  onAddPeople,
  entityType = EntityType.DEAL,
  partnerId,
  addedPartnerContacts,
  onAddPartnerContacts
}) => {
  const { t } = useTranslation();
  const entityName = t(getEnumTKey("entities", "names", EntityType, entityType));
  const [searchTerm, setSearchTerm] = useState("");
  const [newlyCreatedPerson, setNewlyCreatedPerson] = useState<string | null>(
    null
  );
  const [selectedPeople, setSelectedPeople] = useState<string[]>(
    allDealPeople || []
  );
  const [selectedPartnerContacts, setSelectedPartnerContacts] = useState<
    string[]
  >(addedPartnerContacts || []);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const queryClient = useQueryClient();
  const resetPeopleInfinite = useResetPeopleInfinite();
  const updateDeal = useUpdateDealMutation();

  const {
    entities: [companyData]
  } = useCompanies([companyId]);

  // Build filter to include people from both the company and its parent company
  const companyFilter = useMemo(() => {
    const companyIds = [companyId];
    if (companyData?.parentCompany) {
      companyIds.push(companyData.parentCompany);
    }
    return companyIds.length > 1
      ? { companyId: { in: companyIds } }
      : { companyId };
  }, [companyId, companyData?.parentCompany]);

  const {
    entities: people,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isError
  } = usePeopleInfinite(
    companyFilter,
    searchTerm ? { term: searchTerm } : undefined,
    undefined,
    undefined,
    "firstName"
  );

  const {
    entities: [dealData]
  } = useDeals(onAddPeople ? [] : [entityId || ""]);

  // Deal has a linked partner: surface its contacts in the same picker,
  // alongside company people, per CRM-8571.
  const partnerFilter = useMemo(
    () => (partnerId ? { [PartnerContactColumn.PARTNER]: [partnerId] } : {}),
    [partnerId]
  );

  const {
    entities: partnerContacts,
    isLoading: isPartnerContactsLoading,
    hasNextPage: hasNextPartnerContactsPage,
    isFetchingNextPage: isFetchingNextPartnerContactsPage,
    fetchNextPage: fetchNextPartnerContactsPage,
    isError: isPartnerContactsError
  } = usePartnerContactsInfinite(
    partnerFilter,
    searchTerm ? { term: searchTerm } : undefined,
    undefined,
    undefined,
    "firstName",
    true,
    undefined,
    Boolean(partnerId),
    ComputedFields.MINIMAL
  );

  const canLoadMorePeople = !!hasNextPage && !isFetchingNextPage && !isError;
  const canLoadMorePartnerContacts =
    !!partnerId &&
    !!hasNextPartnerContactsPage &&
    !isFetchingNextPartnerContactsPage &&
    !isPartnerContactsError;

  const handleLoadMore = useCallback(() => {
    if (canLoadMorePeople) {
      fetchNextPage();
    }
    if (canLoadMorePartnerContacts) {
      fetchNextPartnerContactsPage();
    }
  }, [
    canLoadMorePeople,
    canLoadMorePartnerContacts,
    fetchNextPage,
    fetchNextPartnerContactsPage
  ]);

  const { rootRef, sentinelRef } = useInfiniteScrollTrigger({
    canLoadMore: canLoadMorePeople || canLoadMorePartnerContacts,
    onLoadMore: handleLoadMore,
    itemCount: (people?.length ?? 0) + (partnerContacts?.length ?? 0)
  });

  useEffect(() => {
    if (isOpen) {
      setSelectedPeople(allDealPeople || []);
      setSelectedPartnerContacts(addedPartnerContacts || []);
    } else {
      setSearchTerm("");
      setNewlyCreatedPerson(null);
    }
  }, [isOpen, allDealPeople, addedPartnerContacts]);

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleAddPeople = async () => {
    const newlyAddedPeople = selectedPeople.filter(
      (personId) => !(allDealPeople || []).includes(personId)
    );
    const newlyAddedPartnerContacts = selectedPartnerContacts.filter(
      (contactId) => !(addedPartnerContacts || []).includes(contactId)
    );

    if (onAddPeople) {
      await onAddPeople(newlyAddedPeople);
      if (newlyAddedPartnerContacts.length > 0 && onAddPartnerContacts) {
        await onAddPartnerContacts(newlyAddedPartnerContacts);
      }
      setIsOpen(false);
      setTimeout(() => {
        resetPage?.();
      }, 100);
      return;
    }

    updateDeal.mutate(
      {
        pathParams: {
          dealId: entityId
        },
        data: {
          name: dealData?.name,
          manuallyAddedPeople: [
            ...(dealData?.manuallyAddedPeople || []),
            ...newlyAddedPeople
          ],
          manuallyAddedPartnerContacts: [
            ...(dealData?.manuallyAddedPartnerContacts || []),
            ...newlyAddedPartnerContacts
          ]
        }
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["deals", { pathParams: { dealsIds: entityId } }]
          });
          setIsOpen(false);
          setTimeout(() => {
            resetPage?.();
          }, 100);
        }
      }
    );
  };

  // Commented out for now
  // const availablePeople = people.filter(
  //   (person) => person.id !== UNKNOWN_PERSON_ID
  // );
  // const selectablePeople = availablePeople.filter(
  //   (person) => !includes(allDealPeople, person.id)
  // );
  // const selectedSelectablePeople = selectedPeople.filter((personId) =>
  //   selectablePeople.some((person) => person.id === personId)
  // );

  // const isAllSelected =
  //   selectablePeople.length > 0 &&
  //   selectedSelectablePeople.length === selectablePeople.length;
  // const isPartiallySelected =
  //   selectedSelectablePeople.length > 0 &&
  //   selectedSelectablePeople.length < selectablePeople.length;

  // const handleSelectAll = (checked: boolean) => {
  //   if (checked) {
  //     // Add all selectable people to selection (keeping existing selections)
  //     const newSelections = selectablePeople.map((person) => person.id);
  //     setSelectedPeople(union(selectedPeople, newSelections));
  //   } else {
  //     // Remove all selectable people from selection (keeping non-selectable ones)
  //     const selectableIds = selectablePeople.map((person) => person.id);
  //     setSelectedPeople(
  //       selectedPeople.filter((id) => !selectableIds.includes(id))
  //     );
  //   }
  // };

  const handleSelectedPeopleChange = (personId: string, checked: boolean) => {
    setSelectedPeople((prevPeople) =>
      checked ? union(prevPeople, [personId]) : without(prevPeople, personId)
    );
  };

  const handleSelectedPartnerContactChange = (
    contactId: string,
    checked: boolean
  ) => {
    setSelectedPartnerContacts((prevContacts) =>
      checked
        ? union(prevContacts, [contactId])
        : without(prevContacts, contactId)
    );
  };

  const newSelectedPeople = filter(
    selectedPeople,
    (personId) => !includes(allDealPeople, personId)
  );

  const newSelectedPartnerContacts = filter(
    selectedPartnerContacts,
    (contactId) => !includes(addedPartnerContacts, contactId)
  );

  const peopleResults = useMemo(
    () =>
      people
        .filter((person) => !isReservedPersonId(person.id))
        .filter((person) =>
          searchTerm?.length > 2 ? true : !includes(allDealPeople, person.id)
        )
        .sort((a, b) => {
          // First priority
          if (newlyCreatedPerson) {
            if (a.id === newlyCreatedPerson) return -1;
            if (b.id === newlyCreatedPerson) return 1;
          }
          return (
            Number(includes(allDealPeople, b.id)) -
            Number(includes(allDealPeople, a.id))
          );
        }),
    [people, allDealPeople, searchTerm, newlyCreatedPerson]
  );

  const partnerContactResults = useMemo(
    () =>
      partnerId
        ? partnerContacts
            .filter((contact) =>
              searchTerm?.length > 2
                ? true
                : !includes(addedPartnerContacts, contact.id)
            )
            .sort(
              (a, b) =>
                Number(includes(addedPartnerContacts, b.id)) -
                Number(includes(addedPartnerContacts, a.id))
            )
        : [],
    [partnerId, partnerContacts, addedPartnerContacts, searchTerm]
  );

  const isEmptyState =
    searchTerm === "" &&
    !isLoading &&
    !isPartnerContactsLoading &&
    people.length + partnerContacts.length > 0 &&
    people
      .filter((person) => !isReservedPersonId(person.id))
      .every((person) => includes(allDealPeople, person.id)) &&
    partnerContacts.every((contact) =>
      includes(addedPartnerContacts, contact.id)
    );

  return (
    <>
      <Modal
        open={isOpen}
        setIsOpen={setIsOpen}
        contentClassname="w-[35rem]"
        data-test="deals-add-people-modal"
        title={
          <div
            className="flex w-full pr-3 justify-between items-center"
            data-test="deals-add-people-modal-header"
          >
            <Text
              as="div"
              variant="lgSubtitleH1"
              data-test="deals-add-people-modal-title"
            >
              {t("detailedView.peopleTab.addPeopleToDeal", { entityName: entityName.toLowerCase() })}
            </Text>
          </div>
        }
        content={
          isEmptyState ? (
            <div className="flex flex-col items-center justify-center gap-4 py-12 px-2">
              <div className="flex flex-col items-center justify-center">
                <PeopleBadge />
                <Text variant="lgBodySecondary">
                  {t("detailedView.peopleTab.emptyModal", {
                    companyName:
                      companyData?.name || t("entities.names.company"),
                    entityName: entityName.toLowerCase()
                  })}
                </Text>
              </div>
              <Button
                variant="tertiary"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <Add />
                {t("detailedView.peopleTab.createPerson")}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col w-[35rem] h-[60vh]">
              <div tabIndex={0} className="sr-only" />
              <div className="sticky top-0 z-10 px-4 pt-3 pb-2 flex items-center gap-2">
                <SearchInput
                  className="bg-transparent flex-1"
                  value={searchTerm}
                  onChange={(value) => setSearchTerm(value)}
                  placeholder={t(
                    partnerId
                      ? "detailedView.peopleTab.modalSearchWithPartner"
                      : "detailedView.peopleTab.modalSearch",
                    { companyName: companyData?.name || "" }
                  )}
                />
                <Button
                  variant="tertiary"
                  size="lg"
                  onClick={() => setIsCreateModalOpen(true)}
                  data-test="deals-add-people-modal-create-button"
                >
                  <Add />
                  {t("detailedView.peopleTab.createPerson")}
                </Button>
              </div>
              <div
                ref={rootRef}
                className="flex flex-col gap-3 px-4 flex-1 overflow-y-auto"
              >
                <CheckboxGroup fullWidth spacing={0} color="var(--cta)">
                  {/* Commented out for now */}
                  {/* <CheckboxGroupItem
                  id="selectAll"
                  label={t("datatoolbar.filters.selectAll")}
                  checked={isAllSelected || isPartiallySelected}
                  onCheckedChange={handleSelectAll}
                  variant={isPartiallySelected ? "partial" : undefined}
                /> */}
                  {map(peopleResults, (person) => (
                    <PersonCheckbox
                      key={person?.id}
                      person={person}
                      selectedPeople={selectedPeople}
                      handleSelectedPeopleChange={handleSelectedPeopleChange}
                      allDealPeople={allDealPeople}
                      entityName={entityName.toLowerCase()}
                    />
                  ))}
                  {map(partnerContactResults, (contact) => (
                    <PartnerContactCheckbox
                      key={contact?.id}
                      partnerContact={contact}
                      selectedContacts={selectedPartnerContacts}
                      handleSelectedContactChange={
                        handleSelectedPartnerContactChange
                      }
                      addedContacts={addedPartnerContacts}
                      entityName={entityName.toLowerCase()}
                    />
                  ))}
                </CheckboxGroup>
                <div ref={sentinelRef} className="w-full py-4 text-center">
                  {(isLoading ||
                    isFetchingNextPage ||
                    (partnerId &&
                      (isPartnerContactsLoading ||
                        isFetchingNextPartnerContactsPage))) && (
                    <InlineLoader label={t("loading")} />
                  )}
                </div>
              </div>
            </div>
          )
        }
        footer={
          <div
            className="flex justify-end gap-2"
            data-test="deals-add-people-modal-footer"
          >
            <Button
              data-test="deals-add-people-modal-cancel-button"
              variant="secondary"
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
            <Button
              data-test="deals-add-people-modal-submit-button"
              disabled={
                isEmpty(newSelectedPeople) && isEmpty(newSelectedPartnerContacts)
              }
              onClick={handleAddPeople}
            >
              {(() => {
                const totalSelected =
                  newSelectedPeople.length + newSelectedPartnerContacts.length;
                return totalSelected === 1
                  ? t("detailedView.peopleTab.addPersonButton")
                  : t("detailedView.peopleTab.addPeopleButton", {
                      numPeople: totalSelected === 0 ? "" : totalSelected
                    });
              })()}
            </Button>
          </div>
        }
      />
      <CreatePersonModal
        isOpen={isCreateModalOpen}
        setIsOpen={setIsCreateModalOpen}
        companyId={companyId}
        resetPage={resetPeopleInfinite}
        onSuccess={(newPersonId) => {
          setNewlyCreatedPerson(newPersonId);
          setSelectedPeople([...selectedPeople, newPersonId]);
        }}
      />
    </>
  );
};

export default AddPeopleModal;
